/* Reading claims out of a JWT, for display only.

   **The client never verifies a token.** Nothing here checks a signature,
   an issuer, an audience or an expiry-as-authorization. It splits the token on
   dots and base64url-decodes the middle part, which anyone holding the token can
   do — that is what "not encrypted" means.

   The only consumer is the user pill in the header. The backend is the sole
   thing that validates a Milo token (`src/milo/token_verify.py`, against the
   JWKS at https://cardea.rayray.cloud/.well-known/jwks.json), and it is the only
   place a validation decision belongs. If you ever find yourself branching on a
   claim read here to decide whether something is *allowed*, that is the bug:
   move the decision to the server. Presentation, not a security check. */

/** Decode a JWT's payload. Returns null for anything that is not a readable JWT. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padding = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const binary = atob(b64 + padding)
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    // TextDecoder rather than escape/unescape: claims carry real names, and a
    // non-ASCII name decoded byte-wise renders as mojibake.
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/* In preference order. `sub` is a UUID and an ugly thing to show a human, but a
   UUID in the header beats an empty header — it is at least evidence that a
   real token is in hand. */
const NAME_CLAIMS = ['preferred_username', 'name', 'email', 'sub'] as const

/**
 * A display name from a token's claims, or null if none of them are usable.
 *
 * Pass the ID token first: Pocket ID puts `preferred_username` and `name` there,
 * while the access token issued for an RFC 8707 resource may carry little more
 * than `sub`.
 */
export function displayNameFrom(...tokens: (string | null | undefined)[]): string | null {
  for (const token of tokens) {
    if (!token) continue
    const claims = decodeJwtPayload(token)
    if (!claims) continue
    for (const claim of NAME_CLAIMS) {
      const value = claims[claim]
      if (typeof value === 'string' && value.trim() !== '') return value.trim()
    }
  }
  return null
}

/**
 * The `exp` claim as epoch milliseconds, or null.
 *
 * Only a fallback for a token response that omits `expires_in`. Note what is
 * *not* here: nothing derives a token's age from `iat`. Pocket ID's refresh
 * grant preserves the original authorization's `iat` and advances only `exp`, so
 * `exp - iat` grows without bound as refreshes accumulate and any "how old is
 * this token" computed from `iat` is wrong for every refreshed token.
 */
export function expiryFromToken(token: string): number | null {
  const claims = decodeJwtPayload(token)
  const exp = claims?.['exp']
  return typeof exp === 'number' ? exp * 1000 : null
}
